import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../services/loader.service';

@Component({
  selector: 'app-loader',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Top Progress Bar - Responsive -->
    <div
      [class.hidden]="!loaderService.loading()"
      class="fixed top-0 left-0 w-full h-0.5 sm:h-1 bg-sacred/30 dark:bg-stone-700/50 z-[9999] overflow-hidden"
    >
      <div
        class="h-full bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-400 transition-all duration-300 ease-out relative"
        [style.width]="loaderService.progress() + '%'"
      >
        <div
          class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 dark:via-stone-200/40 to-transparent animate-shimmer"
        ></div>
      </div>
    </div>

    <!-- Full-Screen Overlay Loader (for heavy operations) - Responsive -->
    @if(loaderService.loading() && showOverlay){
    <div
      class="fixed inset-0 z-[9998] bg-rooted/40 dark:bg-stone-900/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <!-- Floating Orbs - Responsive sizing -->
      <div class="absolute top-10 left-10 sm:top-20 sm:left-20 w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-bloom/20 dark:bg-fuchsia-500/15 rounded-full blur-3xl animate-pulse"></div>
      <div class="absolute bottom-10 right-10 sm:bottom-20 sm:right-20 w-20 h-20 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-soulfire/15 dark:bg-fuchsia-600/10 rounded-full blur-3xl animate-pulse"></div>
      
      <div
        class="bg-white/95 dark:bg-stone-800/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl dark:shadow-fuchsia-900/20 border border-grounded/20 dark:border-stone-700/50 max-w-xs sm:max-w-sm lg:max-w-md w-full relative z-10"
      >
        <!-- Large animated spinner - Responsive -->
        <div class="flex flex-col items-center gap-4 sm:gap-5 lg:gap-6">
          <div class="relative">
            <!-- Outer rotating ring -->
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full border-3 sm:border-4 border-sacred/40 dark:border-stone-600/60 border-t-soulfire dark:border-t-fuchsia-500 animate-spin"
            ></div>
            <!-- Inner rotating ring -->
            <div
              class="absolute spinner-inner w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border-3 sm:border-4 border-bloom/40 dark:border-fuchsia-400/40 border-b-bloom dark:border-b-fuchsia-400 animate-spin"
              style="animation-direction: reverse; animation-duration: 1.5s;"
            ></div>
            <!-- Center dot -->
            <div
              class="absolute spinner-center w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 rounded-full bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-500 dark:to-fuchsia-400 animate-pulse"
            ></div>
          </div>

          <!-- Loading text - Responsive -->
          <div class="text-center">
            <h3
              class="text-base sm:text-lg font-semibold bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-400 dark:to-fuchsia-300 bg-clip-text text-transparent mb-1 sm:mb-2"
            >
              Sacred Processing
            </h3>
            <p class="text-rooted/70 dark:text-stone-300/80 text-xs sm:text-sm leading-relaxed max-w-xs mx-auto">
              <span class="hidden sm:inline">Please hold space while we align with divine energy...</span>
              <span class="sm:hidden">Aligning with divine energy...</span>
            </p>
          </div>

          <!-- Progress indicator - Responsive -->
          <div class="w-full bg-sacred/30 dark:bg-stone-700 rounded-full h-1.5 sm:h-2">
            <div
              class="bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-400 h-1.5 sm:h-2 rounded-full transition-all duration-300"
              [style.width]="loaderService.progress() + '%'"
            ></div>
          </div>
        </div>
      </div>
    </div>
    }

    <!-- Minimal Bottom-Right Loader (default) - Responsive -->
    @if(loaderService.loading() && !showOverlay){
    <div
      class="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 lg:bottom-6 lg:right-6 z-[9999] flex items-center gap-2 sm:gap-3 bg-white/90 dark:bg-stone-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-lg dark:shadow-fuchsia-900/20 border border-grounded/20 dark:border-stone-700/50"
    >
      <div class="relative">
        <div
          class="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-400 animate-spin"
        >
          <div class="absolute mini-spinner-inner bg-white dark:bg-stone-800 rounded-full"></div>
          <div
            class="absolute mini-spinner-center bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-400 rounded-full opacity-60"
          ></div>
        </div>
        <div
          class="absolute inset-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-sacred/50 dark:border-fuchsia-400/50 animate-ping opacity-40"
        ></div>
      </div>
      <span
        class="text-xs sm:text-sm font-medium bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-400 dark:to-fuchsia-300 bg-clip-text text-transparent whitespace-nowrap"
      >
        <span class="hidden sm:inline">Loading...</span>
        <span class="sm:hidden">Loading...</span>
      </span>
    </div>
    }
  `,
  styles: [
    `
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      .animate-shimmer {
        animation: shimmer 1.5s infinite;
      }

      /* Better spinner borders on mobile */
      .border-3 {
        border-width: 3px;
      }

      /* Spinner positioning classes */
      .spinner-inner {
        top: 0.375rem;
        right: 0.375rem;
        bottom: 0.375rem;
        left: 0.375rem;
      }

      @media (min-width: 640px) {
        .spinner-inner {
          top: 0.5rem;
          right: 0.5rem;
          bottom: 0.5rem;
          left: 0.5rem;
        }
      }

      .spinner-center {
        top: 1.125rem;
        right: 1.125rem;
        bottom: 1.125rem;
        left: 1.125rem;
      }

      @media (min-width: 640px) {
        .spinner-center {
          top: 1.25rem;
          right: 1.25rem;
          bottom: 1.25rem;
          left: 1.25rem;
        }
      }

      @media (min-width: 1024px) {
        .spinner-center {
          top: 1.5rem;
          right: 1.5rem;
          bottom: 1.5rem;
          left: 1.5rem;
        }
      }

      /* Mini spinner positioning */
      .mini-spinner-inner {
        top: 0.125rem;
        right: 0.125rem;
        bottom: 0.125rem;
        left: 0.125rem;
      }

      @media (min-width: 640px) {
        .mini-spinner-inner {
          top: 0.25rem;
          right: 0.25rem;
          bottom: 0.25rem;
          left: 0.25rem;
        }
      }

      .mini-spinner-center {
        top: 0.25rem;
        right: 0.25rem;
        bottom: 0.25rem;
        left: 0.25rem;
      }

      @media (min-width: 640px) {
        .mini-spinner-center {
          top: 0.5rem;
          right: 0.5rem;
          bottom: 0.5rem;
          left: 0.5rem;
        }
      }

      /* Ensure proper text handling */
      .whitespace-nowrap {
        white-space: nowrap;
      }

      /* Responsive positioning adjustments */
      @media (max-width: 640px) {
        .fixed.bottom-3.right-3 {
          bottom: 0.75rem;
          right: 0.75rem;
        }
      }
    `,
  ],
})
export class LoaderComponent {
  // Toggle between minimal and full-screen loader
  showOverlay = false; // Set to true for full-screen overlay

  constructor(public loaderService: LoaderService) { }
}